import React, { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faSync,
  faCheck,
  faCalendarAlt,
  faWallet
} from "@fortawesome/free-solid-svg-icons";
import TransactionModal from "../components/TransactionModal";
import { useMccCodes } from "../contexts/MccContext";

function SpendingHistory() {
  const { t } = useTranslation();
  const { getMccDescription, codes } = useMccCodes(); // Access codes to find category
  
  const getCategory = (mcc) => {
     const codeObj = codes.find(c => c.code === String(mcc));
     return codeObj ? codeObj.category : 'Other'; // Or localize 'Other'
  };
  
  // State
  const [transactions, setTransactions] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncStatus, setSyncStatus] = useState('idle'); // idle, syncing, completed
  const [syncProgress, setSyncProgress] = useState(0); // 0-100
  const [syncMessage, setSyncMessage] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);
  const [selectedTransaction, setSelectedTransaction] = useState(null);

  // Filters
  const [search, setSearch] = useState("");
  const [selectedAccount, setSelectedAccount] = useState("all");
  const [dateRange, setDateRange] = useState([null, null]);
  const [startDate, endDate] = dateRange;

  // Debounced search
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  // Fetch Accounts
  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        const response = await axios.get("/accounts");
        setAccounts(response.data);
      } catch (error) {
        console.error("Failed to fetch accounts", error);
      }
    };
    fetchAccounts();
  }, []);

  // Fetch Transactions
  const fetchTransactions = useCallback(async (reset = false) => {
    if (loading) return; // Prevent multiple calls
    setLoading(true);

    try {
      const offset = reset ? 0 : page * 20;
      const params = {
        limit: 20,
        offset,
        accountId: selectedAccount,
        search: debouncedSearch,
        startDate: startDate ? startDate.toISOString() : undefined,
        endDate: endDate ? endDate.toISOString() : undefined,
      };

      const response = await axios.get("/transactions", { params });
      const { transactions: newTransactions } = response.data;

      if (reset) {
        setTransactions(newTransactions);
        setPage(1); // Next page
      } else {
        setTransactions(prev => [...prev, ...newTransactions]);
        setPage(prev => prev + 1);
      }

      setHasMore(newTransactions.length === 20);

    } catch (error) {
      console.error("Failed to fetch transactions", error);
    } finally {
      setLoading(false);
    }
  }, [selectedAccount, debouncedSearch, startDate, endDate, page]); // Exclude 'loading' to avoid infinite loops usage

  // Trigger fetch when filters change
  useEffect(() => {
    // Reset and fetch
    setPage(0);
    fetchTransactions(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccount, debouncedSearch, startDate, endDate]);

  // Sync Handler
  const handleSync = async () => {
    if (syncStatus === 'syncing') {
       setSyncMessage({ type: 'info', text: t('syncInProgress', 'Синхронізація вже триває.') });
       setTimeout(() => setSyncMessage(null), 3000);
       return;
    }
    
    setSyncStatus('syncing');
    setSyncProgress(0);
    setSyncMessage(null);

    try {
      // Trigger background sync
      const response = await axios.post(`/accounts/sync-transactions`);
      
      if (!response.data.success) {
          // If already in progress, just show info
          setSyncMessage({ type: 'info', text: t('syncInProgress', 'Синхронізація вже триває.') });
      } else {
          // Only show toast for error or if user needs feedback, but here the UI is the feedback
          // setSyncMessage({ type: 'success', text: t('syncStarted', 'Синхронізацію розпочато...') });
      }
      
      // Start polling for status
      const pollInterval = setInterval(async () => {
          try {
              const statusRes = await axios.get('/accounts/sync-status');
              const { syncing, total, processed } = statusRes.data;

              // Update progress
              if (total > 0) {
                  const progress = (processed / total) * 100;
                  setSyncProgress(progress);
              }

              if (!syncing) {
                  clearInterval(pollInterval);
                  setSyncProgress(100);
                  setSyncStatus('completed');
                  
                  // Refresh data
                  fetchTransactions(true); 

                  // Reset to idle after 3 seconds
                  setTimeout(() => {
                      setSyncStatus('idle');
                      setSyncProgress(0);
                  }, 3000);
              }
          } catch (e) {
              console.error("Poll error", e);
              clearInterval(pollInterval); // Stop on error
              setSyncStatus('idle');
              setSyncMessage({ type: 'error', text: t('syncStatusFailed', 'Не вдалося перевірити статус синхронізації') });
          }
      }, 1000); // Check every 1 second for smoother UI updates

    } catch (error) {
      console.error("Sync failed", error);
      setSyncMessage({ type: 'error', text: t('syncFailed', 'Помилка синхронізації') });
      setSyncStatus('idle');
      setTimeout(() => setSyncMessage(null), 5000);
    }
  };

  // Group transactions by date
  const groupedTransactions = transactions.reduce((groups, transaction) => {
    const date = new Date(transaction.time).toLocaleDateString();
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(transaction);
    return groups;
  }, {});

  // Infinite Scroll Observer
  const observer = useRef();
  const lastElementRef = useCallback(node => {
    if (loading) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        fetchTransactions(false);
      }
    });
    if (node) observer.current.observe(node);
  }, [loading, hasMore, fetchTransactions]);


  const headerActionsTarget = document.getElementById("header-actions");

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="space-y-6"
    >
      
      {/* Header Sync Button Portal */}
      {headerActionsTarget && createPortal(
        <div className="relative flex items-center justify-center w-12 h-12">
            {/* Circular Progress (only visible when syncing) */}
            {syncStatus === 'syncing' && (
                <div className="absolute inset-0">
                    <svg className="w-full h-full transform -rotate-90">
                        <circle
                            cx="24"
                            cy="24"
                            r="22"
                            fill="transparent"
                            stroke="currentColor"
                            strokeWidth="4"
                            className="text-gray-200 dark:text-gray-700"
                        />
                        <circle
                            cx="24"
                            cy="24"
                            r="22"
                            fill="transparent"
                            stroke="currentColor"
                            strokeWidth="4"
                            strokeDasharray={2 * Math.PI * 22}
                            strokeDashoffset={2 * Math.PI * 22 * (1 - syncProgress / 100)}
                            className="text-primary-500 transition-all duration-500 ease-out"
                        />
                    </svg>
                </div>
            )}

            <button
            type="button"
            onClick={handleSync}
            disabled={syncStatus !== 'idle'}
            className={`w-10 h-10 rounded-full flex items-center justify-center transition-all z-10 ${
                syncStatus === 'completed'
                    ? "bg-green-500 text-white"
                    : syncStatus === 'syncing'
                    ? "bg-white dark:bg-dark-surface text-primary-500" // Transparent/white center for progress ring
                    : "text-primary-600 bg-primary-50 hover:bg-primary-100 dark:bg-primary-900/20 dark:hover:bg-primary-900/30 shadow-sm"
            }`}
            title={t("syncFromMonobank", "Синхронізувати з Monobank")}
            >
             <AnimatePresence mode="wait">
                {syncStatus === 'completed' ? (
                    <motion.div
                        key="check"
                        initial={{ scale: 0, rotate: -45 }}
                        animate={{ scale: 1, rotate: 0 }}
                        exit={{ scale: 0 }}
                    >
                        <FontAwesomeIcon icon={faCheck} size="sm" />
                    </motion.div>
                ) : (
                    <motion.div
                        key="sync"
                        animate={syncStatus === 'syncing' ? { rotate: 360 } : {}}
                        transition={syncStatus === 'syncing' ? { repeat: Infinity, duration: 1, ease: "linear" } : {}}
                    >
                        <FontAwesomeIcon icon={faSync} size="sm" />
                    </motion.div>
                )}
             </AnimatePresence>
            </button>
        </div>,
        headerActionsTarget
      )}

      {/* Sync Message Toast - Fixed Position */}
      <AnimatePresence>
        {syncMessage && (
           <motion.div
             initial={{ opacity: 0, y: -20, x: '-50%' }}
             animate={{ opacity: 1, y: 0, x: '-50%' }}
             exit={{ opacity: 0, y: -20, x: '-50%' }}
             className={`fixed top-24 left-1/2 z-[100] p-4 rounded-xl shadow-lg border backdrop-blur-md min-w-[300px] text-center ${
               syncMessage.type === 'success' 
                 ? 'bg-green-50/90 border-green-200 text-green-700 dark:bg-green-900/80 dark:border-green-800 dark:text-green-300' 
                 : syncMessage.type === 'error'
                 ? 'bg-red-50/90 border-red-200 text-red-700 dark:bg-red-900/80 dark:border-red-800 dark:text-red-300'
                 : 'bg-blue-50/90 border-blue-200 text-blue-700 dark:bg-blue-900/80 dark:border-blue-800 dark:text-blue-300'
             }`}
           >
             {syncMessage.text}
           </motion.div>
        )}
      </AnimatePresence>

      
      {/* Controls Card */}
      <div className="card bg-white dark:bg-dark-surface p-6 shadow-sm">
        
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
          
          {/* Search */}
          <div className="md:col-span-5 relative">
            <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder={t("search", "Пошук транзакцій...")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-gray-50 dark:bg-gray-800 border-none rounded-xl focus:ring-2 focus:ring-primary-500 transition-all"
            />
          </div>

          {/* Account Filter */}
          <div className="md:col-span-3">
             <div className="relative">
                <FontAwesomeIcon icon={faWallet} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <select
                  value={selectedAccount}
                  onChange={(e) => setSelectedAccount(e.target.value)}
                  className="w-full pl-10 pr-8 py-2 bg-gray-50 dark:bg-gray-800 border-none rounded-xl focus:ring-2 focus:ring-primary-500 appearance-none cursor-pointer"
                >
                  <option value="all">{t("allAccounts", "Всі рахунки")}</option>
                  {accounts.filter(acc => acc.monobankAccountId).map(acc => (
                    <option key={acc.id} value={acc.id}>{acc.name}</option>
                  ))}
                </select>
             </div>
          </div>

          {/* Date Picker */}
          <div className="md:col-span-4 relative z-50">
             <div className="relative w-full">
              <DatePicker
                selected={startDate}
                onChange={(update) => setDateRange(update)}
                startDate={startDate}
                endDate={endDate}
                selectsRange
                className="w-full pl-10 pr-4 py-2 bg-gray-50 dark:bg-gray-800 border-none rounded-xl focus:ring-2 focus:ring-primary-500 text-sm"
                placeholderText={t("selectDate", "Виберіть дату")}
              />
              <FontAwesomeIcon icon={faCalendarAlt} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
             </div>
          </div>
        </div>
      </div>

      {/* Transactions List */}
      <div className="space-y-6">
        {Object.keys(groupedTransactions).length === 0 && !loading ? (
           <div className="text-center py-12 text-gray-500">
             <p>{t("noTransactions", "Транзакцій не знайдено")}</p>
           </div>
        ) : (
          Object.entries(groupedTransactions).map(([date, txs]) => (
            <div key={date} className="animate-in fade-in slide-in-from-bottom-4 duration-500">
              <div className="flex items-center gap-4 mb-3 px-2">
                 <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{date}</h3>
                 <div className="h-px flex-1 bg-gray-200 dark:bg-gray-800"></div>
              </div>
              
              <div className="bg-white dark:bg-dark-surface rounded-2xl shadow-sm overflow-hidden border border-gray-100 dark:border-gray-800">
                {txs.map((tx) => {
                  const code = String(tx.originalMcc || tx.mcc);
                  const isKnown = codes.some(c => c.code === code);
                  const mccDescription = getMccDescription(code);
                  
                  return (
                  <div 
                    key={tx.id}
                    onClick={() => setSelectedTransaction(tx)}
                    className="group relative p-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer transition-colors border-b border-gray-100 dark:border-gray-800 last:border-0 overflow-hidden"
                  >
                    {/* Indicator Line */}
                    <div className={`absolute left-0 top-1/2 -translate-y-1/2 w-[4px] rounded-full h-[calc(100%-16px)] ${
                      tx.amount < 0 ? 'bg-red-500' : 'bg-green-500'
                    }`} />

                    <div className="flex items-center justify-between pl-1">
                      <div className="flex items-center gap-4">
                        <div>
                          <p className="font-semibold text-gray-900 dark:text-white" title={tx.description}>
                            {tx.description.length > 15 ? `${tx.description.substring(0, 15)}...` : tx.description}
                          </p>
                          <div className="flex items-center gap-2 text-xs text-gray-500 mt-1">
                            <span title={mccDescription}>
                              {(() => {
                                const display = isKnown ? mccDescription : t('unknownCategory', 'Невідома категорія');
                                return display.length > 13 ? `${display.substring(0, 13)}...` : display;
                              })()}
                            </span>
        
                            <span className="px-2 py-0.5 rounded-full bg-primary-50 text-primary-600 dark:bg-primary-900/20 dark:text-primary-400 text-[10px] font-medium border border-primary-100 dark:border-primary-800">
                              {tx.accountName}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="text-right shrink-0">
                        <p className={`font-bold ${tx.amount < 0 ? "text-gray-900 dark:text-white" : "text-green-600 dark:text-green-400"} whitespace-nowrap`}>
                          {(tx.amountEur / 100).toFixed(2)} <span className="text-xs text-gray-500">EUR</span>
                        </p>
                        <p className="text-xs text-gray-400">
                          {new Date(tx.time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                    </div>
                  </div>
                )})}
              </div>
            </div>
          ))
        )}
        
        {/* Loading Indicator */}
        <div ref={lastElementRef} className="py-8 flex justify-center">
           {loading && (
             <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
           )}
        </div>
      </div>

      <AnimatePresence>
        {selectedTransaction && (
          <TransactionModal 
            transaction={selectedTransaction} 
            onClose={() => setSelectedTransaction(null)} 
          />
        )}
      </AnimatePresence>

    </motion.div>
  );
}

export default SpendingHistory;
